import * as Yup from 'yup';
import { Address, AddressType, CountryType, PlaceOfServiceType, ProviderType, StateType } from '../../api';
import { normalisePaperValue } from '../payer-routing/payer-paper-write';
import { rosterIsValidEin, rosterIsValidNpi } from './roster.identifiers';
import type { RosterFacility, RosterFacilityDraft, RosterProvider, RosterProviderDraft } from './roster.types';

/**
 * The Roster drawer's form: values as typed, the rules they must pass, and the conversion to and
 * from records (FE-109). No React, no barrel — the rules run in a node test (NS-11: yup for forms).
 *
 * The rules are the API's where the API states them, and nothing stricter:
 * - `npi` and `type` are required on both provider payloads (`SupportCreateProviderPayload`,
 *   `SupportUpdateProviderPayload`); a facility's `name` is required, its NPI is not.
 * - phone and fax are "in E.164 format" per the models; what people type is normalised to it with
 *   the same rule the payer editor uses (`normalisePaperValue`).
 * - an address needs a name — the API answers 400 `must not be empty` without one — and a country,
 *   without which it answers 500 (both measured on the payer work, 2026-09-15). Country is always
 *   US here, as it is everywhere in this app.
 */

/** The address types a provider or facility can carry. The `PAYER_*` purposes belong to payers. */
export const ROSTER_ADDRESS_TYPES: AddressType[] = [AddressType.MAILING, AddressType.PHYSICAL, AddressType.PAYTO];

export interface IRosterAddressValues {
    type: AddressType;
    name: string;
    line1: string;
    line2: string;
    /**
     * Lines past the second, as the API has them. The drawer shows two lines — claims carry two
     * (X12 N3) — but a record can hold more, and an edit sends the whole address back, so these ride
     * along unchanged rather than being dropped by a save that never touched them.
     */
    extraLines?: string[];
    city: string;
    state: string;
    postal: string;
}

export interface IRosterProviderValues {
    type: ProviderType | '';
    organization: string;
    first: string;
    middle: string;
    last: string;
    suffix: string;
    npi: string;
    ein: string;
    ssn: string;
    ptan: string;
    license: string;
    taxonomyCode: string;
    taxonomyQualifier: string;
    email: string;
    phone: string;
    fax: string;
    halt: boolean;
    addresses: IRosterAddressValues[];
}

export interface IRosterFacilityValues {
    name: string;
    nickname: string;
    npi: string;
    placeOfServiceCode: PlaceOfServiceType | '';
    ein: string;
    ssn: string;
    other: string;
    description: string;
    addresses: IRosterAddressValues[];
}

export const emptyRosterAddress = (name = ''): IRosterAddressValues => ({
    type: AddressType.MAILING,
    name,
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal: '',
});

const text = (value: string | undefined): string => (value ?? '').trim();
const optional = (value: string | undefined): string | undefined => text(value) || undefined;
const E164 = /^\+[1-9]\d{1,14}$/;

/** What the drawer shows for an existing provider, or a blank one. */
export function rosterProviderValues(provider?: RosterProvider): IRosterProviderValues {
    return {
        type: provider?.type ?? ProviderType.RENDERING,
        organization: provider?.organization ?? '',
        first: provider?.first ?? '',
        middle: provider?.middle ?? '',
        last: provider?.last ?? '',
        suffix: provider?.suffix ?? '',
        npi: provider?.npi ?? '',
        ein: provider?.ein ?? '',
        ssn: provider?.ssn ?? '',
        ptan: provider?.ptan ?? '',
        license: provider?.license ?? '',
        taxonomyCode: provider?.taxonomyCode ?? '',
        taxonomyQualifier: provider?.taxonomyQualifier ?? 'ZZ',
        email: provider?.email ?? '',
        phone: provider?.phone ?? '',
        fax: provider?.fax ?? '',
        halt: provider?.halt ?? false,
        addresses: (provider?.addresses ?? []).map(addressValues),
    };
}

export function rosterFacilityValues(facility?: RosterFacility): IRosterFacilityValues {
    return {
        name: facility?.name ?? '',
        nickname: facility?.nickname ?? '',
        npi: facility?.npi ?? '',
        placeOfServiceCode: facility?.placeOfServiceCode ?? '',
        ein: facility?.ein ?? '',
        ssn: facility?.ssn ?? '',
        other: facility?.other ?? '',
        description: facility?.description ?? '',
        addresses: (facility?.addresses ?? []).map(addressValues),
    };
}

function addressValues(address: Address): IRosterAddressValues {
    const extraLines = address.lines?.slice(2) ?? [];
    return {
        type: address.type ?? AddressType.MAILING,
        name: address.name ?? '',
        line1: address.lines?.[0] ?? '',
        line2: address.lines?.[1] ?? '',
        ...(extraLines.length ? { extraLines } : {}),
        city: address.city ?? '',
        state: address.state ?? '',
        postal: address.postal ?? '',
    };
}

function toAddress(values: IRosterAddressValues): Address {
    return {
        type: values.type,
        name: text(values.name),
        lines: [values.line1, values.line2, ...(values.extraLines ?? [])].map(text).filter(Boolean),
        city: text(values.city),
        state: text(values.state) as StateType,
        postal: text(values.postal),
        country: CountryType.US,
    };
}

/** Phone and fax as the API stores them. A value that cannot be normalised is left as typed, so validation rejects it. */
export function rosterPhoneValue(value: string): string {
    return text(value) ? normalisePaperValue('Fax', value) : '';
}

/**
 * The draft to save. Blank fields are omitted rather than sent as empty strings.
 *
 * Automation follows the support provider dialog exactly (`providers-dialog.tsx`, 28737c433,
 * confirmed intentional 2026-09-25): on every save a BILLING provider has Professionals and
 * Payments automation set OFF, and every other type has automation cleared. The other flags —
 * `enterCoding`, `vendorCoding`, `institutional` — are carried over untouched. Two screens that
 * saved the same record differently would undo each other silently, so this one does not differ.
 */
export function rosterProviderDraft(values: IRosterProviderValues, existing?: RosterProvider): RosterProviderDraft {
    const type = values.type as ProviderType;
    return {
        id: existing?.id,
        type,
        organization: optional(values.organization),
        first: optional(values.first),
        middle: optional(values.middle),
        last: optional(values.last),
        suffix: optional(values.suffix),
        npi: optional(values.npi),
        ein: optional(values.ein),
        ssn: optional(values.ssn),
        ptan: optional(values.ptan),
        license: optional(values.license),
        taxonomyCode: optional(values.taxonomyCode)?.toUpperCase(),
        taxonomyQualifier: optional(values.taxonomyQualifier),
        email: optional(values.email),
        phone: optional(rosterPhoneValue(values.phone)),
        fax: optional(rosterPhoneValue(values.fax)),
        halt: values.halt,
        automation:
            type === ProviderType.BILLING
                ? {
                      ...existing?.automation,
                      professional: false,
                      payment: false,
                  }
                : undefined,
        addresses: values.addresses.map(toAddress),
    };
}

export function rosterFacilityDraft(values: IRosterFacilityValues, existing?: RosterFacility): RosterFacilityDraft {
    return {
        id: existing?.id,
        name: text(values.name),
        nickname: optional(values.nickname),
        npi: optional(values.npi),
        placeOfServiceCode: (values.placeOfServiceCode || undefined) as PlaceOfServiceType | undefined,
        ein: optional(values.ein),
        ssn: optional(values.ssn),
        other: optional(values.other),
        description: optional(values.description),
        addresses: values.addresses.map(toAddress),
    };
}

const addressSchema = Yup.object().shape({
    type: Yup.mixed<AddressType>().oneOf(ROSTER_ADDRESS_TYPES).required('Address type is required'),
    name: Yup.string().trim().required('Name is required'),
    line1: Yup.string().trim().required('Line 1 is required'),
    line2: Yup.string(),
    city: Yup.string().trim().required('City is required'),
    state: Yup.string().trim().required('State is required'),
    postal: Yup.string()
        .trim()
        .required('Postal code is required')
        .matches(/^\d{5}(-?\d{4})?$/, 'Postal code is 5 or 9 digits'),
});

const npiRule = Yup.string()
    .trim()
    .test('npi', 'NPI is 10 digits with a valid check digit', value => !value || rosterIsValidNpi(value));
const einRule = Yup.string()
    .trim()
    .test('ein', 'EIN is 9 digits', value => !value || rosterIsValidEin(value));
const ssnRule = Yup.string()
    .trim()
    .matches(/^\d{3}-?\d{2}-?\d{4}$/, { message: 'SSN is 9 digits', excludeEmptyString: true });
const phoneRule = (label: string) =>
    Yup.string().test('e164', `${label} must be a full phone number, e.g. 404-555-0123`, value =>
        !text(value) ? true : E164.test(rosterPhoneValue(value as string)),
    );

export const rosterProviderSchema = Yup.object().shape({
    type: Yup.mixed<ProviderType>()
        .oneOf(Object.values(ProviderType), 'Provider type is required')
        .required('Provider type is required'),
    organization: Yup.string().test('named', 'Enter an organization or a last name', function (value) {
        return text(value) !== '' || text((this.parent as IRosterProviderValues).last) !== '';
    }),
    last: Yup.string(),
    npi: npiRule.required('NPI is required'),
    ein: einRule,
    ssn: ssnRule,
    taxonomyCode: Yup.string()
        .trim()
        .matches(/^[0-9A-Za-z]{9}[Xx]$/, {
            message: 'Taxonomy code is 10 characters ending in X',
            excludeEmptyString: true,
        }),
    email: Yup.string().trim().email('Email is not a valid address'),
    phone: phoneRule('Phone'),
    fax: phoneRule('Fax'),
    addresses: Yup.array().of(addressSchema),
});

export const rosterFacilitySchema = Yup.object().shape({
    name: Yup.string().trim().required('Name is required'),
    npi: npiRule,
    ein: einRule,
    ssn: ssnRule,
    addresses: Yup.array().of(addressSchema),
});
